import { TopupAttentionComponent } from "../topup-attention/topup-attention.component";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { NotificationNavigationService } from 'app/core/services/navigation/notification-navigation.service';
import { SharedModule } from "shared-ui/shared.module";
import { DxPopoverModule } from "devextreme-angular/ui/popover";
import { EMPTY, Subject, Subscription, catchError, exhaustMap, takeUntil, timer } from "rxjs";

import { NotificationInboxItem } from "shared-domains/domains/notification/notification.models";
import { NotificationInboxService } from "app/core/services/api/notification/notification-inbox.service";
import { NotificationRealtimeService, NotificationRealtimeState } from "app/core/services/realtime/notification-realtime.service";
import { NotificationContentPipe } from "app/shared/pipes/notification-content.pipe";
import { NotificationSubjectPipe } from "app/shared/pipes/notification-subject.pipe";
import { TranslocoService } from "@jsverse/transloco";

/**
 * LUL-653 — persistent notification inbox (AC 7): bell + unread badge, danh sách, mark read/all,
 * deep-link tới đối tượng nghiệp vụ. Poll unread 60s (SignalR push là tối ưu mở rộng).
 */
@Component({
    selector: "notification-inbox",
    templateUrl: "./notification-inbox.component.html",
    styleUrls: ["./notification-inbox.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [TopupAttentionComponent, SharedModule, DxPopoverModule, NotificationContentPipe, NotificationSubjectPipe],
})
export class NotificationInboxComponent implements OnInit, OnDestroy {
    private static readonly FALLBACK_INITIAL_DELAY_MS = 0;
    private static readonly FALLBACK_POLL_INTERVAL_MS = 5 * 60_000;

    // -----------------------------------------------------------------------------------------------------
    // @ Fields — inbox state & realtime status
    // -----------------------------------------------------------------------------------------------------
    public items: NotificationInboxItem[] = [];
    public unreadCount = 0;
    public panelVisible = false;
    public realtimeState: NotificationRealtimeState = "disconnected";
    public loadFailed = false;

    private readonly _destroy$ = new Subject<void>();
    private panelLoad?: Subscription;

    // -----------------------------------------------------------------------------------------------------
    // @ Constructor
    // -----------------------------------------------------------------------------------------------------
    constructor(
        private service: NotificationInboxService,
        private realtime: NotificationRealtimeService,
        public navigation: NotificationNavigationService,
        private cdr: ChangeDetectorRef,
        private transloco: TranslocoService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods — display helpers
    // -----------------------------------------------------------------------------------------------------

    /** Xem ghi chú ở NotificationListComponent.severityLabel — P1..P4 không có nghĩa với người dùng. */
    severityLabel(item: NotificationInboxItem): string {
        const key = `notificationInbox.severity.${item.Severity}`;
        const translated = this.transloco.translate(key);
        return translated && translated !== key ? translated : item.SeverityLabel;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        this.service.unreadCount$.pipe(takeUntil(this._destroy$)).subscribe((count) => {
            this.unreadCount = count;
            this.cdr.markForCheck();
        });

        this.realtime.state$.pipe(takeUntil(this._destroy$)).subscribe((state) => {
            this.realtimeState = state;
            this.cdr.markForCheck();
        });
        this.realtime.unreadCount$.pipe(takeUntil(this._destroy$)).subscribe((count) => this.service.setRealtimeUnreadCount(count));
        this.realtime.notification$.pipe(takeUntil(this._destroy$)).subscribe((payload) => {
            const incoming = new NotificationInboxItem(payload);
            if (!this.items.some((item) => item.NotificationDeliveryId === incoming.NotificationDeliveryId)) {
                this.items = [incoming, ...this.items].slice(0, 20);
                this.unreadCount += incoming.IsRead ? 0 : 1;
                this.service.setRealtimeUnreadCount(this.unreadCount);
                this.cdr.markForCheck();
            }
        });
        this.realtime.start().catch(() => undefined);

        // Always perform an immediate read. A connected SignalR transport is not proof that the connection
        // was bound to an account, and realtime events can be missed while a tab is suspended. The slow
        // periodic read is therefore also a convergence mechanism, not only a transport fallback.
        timer(NotificationInboxComponent.FALLBACK_INITIAL_DELAY_MS, NotificationInboxComponent.FALLBACK_POLL_INTERVAL_MS)
            .pipe(
                exhaustMap(() => this.service.listMyNotifications(false, 0, 20).pipe(
                    catchError(() => {
                        this.loadFailed = true;
                        this.cdr.markForCheck();
                        return EMPTY;
                    }),
                )),
                takeUntil(this._destroy$),
            )
            .subscribe((items) => {
                this.items = items;
                this.loadFailed = false;
                this.cdr.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this.panelLoad?.unsubscribe();
        void this.realtime.stop().catch(() => undefined);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods — event handlers / UI actions
    // -----------------------------------------------------------------------------------------------------

    togglePanel(): void {
        this.panelVisible = !this.panelVisible;
        if (this.panelVisible) {
            this.panelLoad?.unsubscribe();
            this.panelLoad = this.service.listMyNotifications(false, 0, 20).subscribe({
                next: (items) => {
                    this.items = items;
                    this.loadFailed = false;
                    this.cdr.markForCheck();
                },
                error: () => {
                    this.loadFailed = true;
                    this.cdr.markForCheck();
                },
            });
        }
        this.cdr.markForCheck();
    }

    async open(item: NotificationInboxItem): Promise<void> {
        if (await this.navigation.open(item)) this.panelVisible = false;
        this.cdr.markForCheck();
    }

    markAllRead(): void {
        this.service.markAllRead().subscribe(() => {
            this.items = this.items.map((i) => Object.assign(i, { IsRead: true }));
            this.cdr.markForCheck();
        });
    }

    async viewAll(): Promise<void> {
        if (await this.navigation.viewAll()) this.panelVisible = false;
        this.cdr.markForCheck();
    }
}
